import { create } from "zustand";
import {
  createSavedConnection as apiCreateSavedConnection,
  deleteSavedConnection as apiDeleteSavedConnection,
  getConnectionHealth as apiGetConnectionHealth,
  isPostgresLike,
  listSavedConnections as apiListSavedConnections,
  updateSavedConnection as apiUpdateSavedConnection,
  type ConnectionType,
  type CreateSavedConnectionRequest,
  type SavedConnection,
  type SavedConnectionScope,
  type UpdateSavedConnectionRequest,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("connectionsStore");

const FRESHNESS_WINDOW_MS = 30_000;

/** Each probe opens a real connection to the user's external DB, so
 *  results stay valid much longer than the list itself. */
const HEALTH_FRESHNESS_WINDOW_MS = 5 * 60_000;

export interface ConnectionHealthEntry {
  state: "checking" | "reachable" | "unreachable";
  reason?: string;
  /** Client clock, unix ms. Drives both the staleness gate and the
   *  "checked Nm ago" tooltip text. */
  checkedAt?: number;
}

const scopeKey = (scope: SavedConnectionScope): string =>
  scope.modelId ? `model:${scope.modelId}` : `org:${scope.orgId ?? ""}`;

interface ScopedList {
  scope: SavedConnectionScope;
  /** `null` distinguishes "never loaded" from "empty". */
  list: SavedConnection[] | null;
  fetchedAt: number | null;
  error: string | null;
}

interface ConnectionsState {
  /** By `scopeKey`. The canvas shows the model's list in the Import picker
   *  beside every org's in breadcrumbs and export, so a shared slot would hold
   *  whichever answered last. */
  lists: Record<string, ScopedList>;
  /** Any scope. */
  inFlight: boolean;
  /** Keyed by connection id. Absent entry = never checked / not checkable. */
  health: Record<string, ConnectionHealthEntry>;
}

interface ConnectionsActions {
  fetchIfStale: (scope?: SavedConnectionScope) => Promise<void>;
  /** With no scope, refreshes every list a view has asked for (never a new,
   *  wider one): after a create or an OAuth round trip, any of them may be
   *  missing the new connection. */
  refetch: (scope?: SavedConnectionScope) => Promise<void>;
  create: (req: CreateSavedConnectionRequest) => Promise<SavedConnection>;
  update: (
    id: string,
    patch: UpdateSavedConnectionRequest,
  ) => Promise<SavedConnection>;
  remove: (id: string) => Promise<void>;
  /** Probes Postgres/Supabase connections whose last result is older
   *  than the health window. In-memory only — a reload re-probes. */
  checkHealthIfStale: (connections: SavedConnection[]) => Promise<void>;
}

type ConnectionsStore = ConnectionsState & ConnectionsActions;

const loads = new Map<string, Promise<void>>();

export const useConnectionsStore = create<ConnectionsStore>((set, get) => ({
  lists: {},
  inFlight: false,
  health: {},

  fetchIfStale: async (scope = {}) => {
    const key = scopeKey(scope);
    const pending = loads.get(key);
    if (pending) return pending;
    const fetchedAt = get().lists[key]?.fetchedAt ?? null;
    if (fetchedAt !== null && Date.now() - fetchedAt < FRESHNESS_WINDOW_MS) {
      return;
    }
    await get().refetch(scope);
  },

  refetch: async (scope) => {
    const scopes = scope
      ? [scope]
      : Object.values(get().lists).map((e) => e.scope);
    await Promise.all(scopes.map((s) => load(set, s)));
  },

  create: async (req) => {
    const created = await apiCreateSavedConnection(req);
    // A connection carries no org, so only the server knows which lists hold it.
    await get().refetch();
    return created;
  },

  update: async (id, patch) => {
    const updated = await apiUpdateSavedConnection(id, patch);
    patchLists(set, (list) => list.map((c) => (c.id === id ? updated : c)));
    // Credentials may have changed — drop the stale verdict so the next
    // checkHealthIfStale probes live.
    dropHealthEntry(set, id);
    return updated;
  },

  remove: async (id) => {
    await apiDeleteSavedConnection(id);
    patchLists(set, (list) => list.filter((c) => c.id !== id));
    dropHealthEntry(set, id);
  },

  checkHealthIfStale: async (connections) => {
    const now = Date.now();
    const { health } = get();
    const targets = connections.filter((c) => {
      if (!isPostgresLike(c.credentials)) return false;
      const entry = health[c.id];
      if (entry?.state === "checking") return false;
      return (
        entry?.checkedAt === undefined ||
        now - entry.checkedAt >= HEALTH_FRESHNESS_WINDOW_MS
      );
    });
    if (targets.length === 0) return;
    set((s) => ({
      health: {
        ...s.health,
        ...Object.fromEntries(
          targets.map((c) => [c.id, { state: "checking" } as const]),
        ),
      },
    }));
    await Promise.allSettled(
      targets.map(async (c) => {
        try {
          const result = await apiGetConnectionHealth(c.id);
          if (result.status === "not_applicable") {
            dropHealthEntry(set, c.id);
            return;
          }
          set((s) => ({
            health: {
              ...s.health,
              [c.id]: {
                state: result.status,
                reason:
                  result.status === "unreachable" ? result.reason : undefined,
                checkedAt: Date.now(),
              },
            },
          }));
        } catch (e) {
          log.error("health check failed for %s: %o", c.id, e);
          // A transport failure says nothing about the external DB —
          // drop the entry (no dot) rather than show a false verdict.
          dropHealthEntry(set, c.id);
        }
      }),
    );
  },
}));

type SetState = (
  fn: (s: ConnectionsState) => Partial<ConnectionsState>,
) => void;

/** Supersedes a load of the same scope already out, which may have been
 *  answered before a write landed; whoever awaits that one waits for this. */
function load(set: SetState, scope: SavedConnectionScope): Promise<void> {
  const key = scopeKey(scope);
  const request: Promise<void> = apiListSavedConnections(scope)
    .then(
      (list) => {
        if (loads.get(key) !== request) return loads.get(key);
        set((s) => ({
          lists: {
            ...s.lists,
            [key]: { scope, list, fetchedAt: Date.now(), error: null },
          },
        }));
      },
      (e) => {
        if (loads.get(key) !== request) return loads.get(key);
        log.error("refetch failed: %o", e);
        const error =
          e instanceof Error ? e.message : "Failed to load connections";
        // Keep last-known-good list visible; only surface the error.
        set((s) => {
          const prev: ScopedList | undefined = s.lists[key];
          return {
            lists: {
              ...s.lists,
              [key]: {
                scope,
                list: prev?.list ?? null,
                fetchedAt: prev?.fetchedAt ?? null,
                error,
              },
            },
          };
        });
      },
    )
    .finally(() => {
      if (loads.get(key) === request) loads.delete(key);
      set(() => ({ inFlight: loads.size > 0 }));
    });
  loads.set(key, request);
  // Listed before its first answer, so a create meanwhile refetches it too.
  set((s) => ({
    inFlight: true,
    lists:
      key in s.lists
        ? s.lists
        : {
            ...s.lists,
            [key]: { scope, list: null, fetchedAt: null, error: null },
          },
  }));
  return request;
}

function patchLists(
  set: SetState,
  patch: (list: SavedConnection[]) => SavedConnection[],
) {
  set((s) => ({
    lists: Object.fromEntries(
      Object.entries(s.lists).map(([key, e]) => [
        key,
        e.list === null ? e : { ...e, list: patch(e.list) },
      ]),
    ),
  }));
}

function dropHealthEntry(set: SetState, id: string) {
  set((s) => {
    if (!(id in s.health)) return {};
    const health = { ...s.health };
    delete health[id];
    return { health };
  });
}

/** The list fetched under `scope`, every org's by default; `null` until it lands. */
export const selectList =
  (scope: SavedConnectionScope = {}) =>
  (s: ConnectionsState): SavedConnection[] | null =>
    s.lists[scopeKey(scope)]?.list ?? null;

export const selectLoadError =
  (scope: SavedConnectionScope = {}) =>
  (s: ConnectionsState): string | null =>
    s.lists[scopeKey(scope)]?.error ?? null;

/** A connection is the same in every list that holds it. */
export const findConnection = (
  s: ConnectionsState,
  id: string,
): SavedConnection | undefined =>
  Object.values(s.lists)
    .flatMap((e) => e.list ?? [])
    .find((c) => c.id === id);

// Selectors — called on a list snapshot, not via `useConnectionsStore(selector)`.

export const selectById = (
  list: SavedConnection[] | null,
  id: string,
): SavedConnection | undefined => list?.find((c) => c.id === id);

export const selectByType = (
  list: SavedConnection[] | null,
  type: ConnectionType,
): SavedConnection[] => (list ?? []).filter((c) => c.credentials.type === type);

export const selectByHostFragment = (
  list: SavedConnection[] | null,
  fragment: string,
): SavedConnection[] => {
  if (!fragment) return list ?? [];
  const needle = fragment.toLowerCase();
  return (list ?? []).filter((c) => {
    if (c.display_name.toLowerCase().includes(needle)) return true;
    const cr = c.credentials;
    if (cr.type === "google_oauth") {
      return cr.account_email.toLowerCase().includes(needle);
    }
    if (cr.type === "bucket") {
      if (cr.provider.toLowerCase().includes(needle)) return true;
      if (cr.scope.kind === "bucket") {
        return cr.scope.name.toLowerCase().includes(needle);
      }
      return false;
    }
    if (cr.type === "local_sqlite") {
      // Local SQLite has no host/database/username — display_name is
      // already matched above, hash search isn't useful for humans.
      return false;
    }
    return (
      cr.host.toLowerCase().includes(needle) ||
      cr.database.toLowerCase().includes(needle) ||
      cr.username.toLowerCase().includes(needle)
    );
  });
};

export const selectUnusedFor30Days = (
  list: SavedConnection[] | null,
): SavedConnection[] => {
  const cutoff = Date.now() / 1000 - 30 * 24 * 60 * 60;
  return (list ?? []).filter((c) => {
    const ts = c.last_used_at ?? c.created_at;
    return ts < cutoff;
  });
};
